import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

// S3 Configuration from environment variables
const S3_CONFIG = {
  region: process.env.REACT_APP_AWS_REGION || 'us-east-1',
  credentials: {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID || '',
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY || '',
  },
};

const BUCKET_NAME = process.env.REACT_APP_S3_BUCKET || 'linkwell-business-images';

// Create S3 client
const s3Client = new S3Client(S3_CONFIG);

/**
 * Generate a unique filename for the uploaded image
 */
const generateUniqueFilename = (originalFilename: string): string => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const extension = originalFilename.split('.').pop()?.toLowerCase() || 'jpg';
  return `business-images/${timestamp}-${randomString}.${extension}`;
};

/**
 * Get the content type based on file extension
 */
const getContentType = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase();
  const contentTypes: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
  };
  return contentTypes[extension || ''] || 'image/jpeg';
};

/**
 * Upload a file directly to S3 using a pre-signed URL
 * @param file - The file to upload
 * @returns The public URL of the uploaded file
 */
export const uploadImageToS3 = async (file: File): Promise<string> => {
  const key = generateUniqueFilename(file.name);
  const contentType = getContentType(file.name);

  // Create the PutObject command
  const command = new PutObjectCommand({
    Bucket: BUCKET_NAME,
    Key: key,
    ContentType: contentType,
  });

  try {
    // Generate a pre-signed URL for uploading (valid for 5 minutes)
    const presignedUrl = await getSignedUrl(s3Client, command, { expiresIn: 300 });

    // Upload the file using the pre-signed URL
    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': contentType,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed with status: ${uploadResponse.status}`);
    }

    // Return the public URL of the uploaded file
    const publicUrl = `https://${BUCKET_NAME}.s3.${S3_CONFIG.region}.amazonaws.com/${key}`;
    return publicUrl;
  } catch (error) {
    console.error('S3 Upload Error:', error);
    throw new Error('Failed to upload image to S3. Please try again.');
  }
};

/**
 * Upload an image from a data URL (base64) to S3
 * @param dataUrl - The base64 data URL of the image
 * @param filename - Optional filename for the image
 * @returns The public URL of the uploaded file
 */
export const uploadDataUrlToS3 = async (dataUrl: string, filename: string = 'image.jpg'): Promise<string> => {
  // Convert data URL to Blob
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  
  // Create a File object from the Blob
  const file = new File([blob], filename, { type: blob.type });
  
  return uploadImageToS3(file);
};

/**
 * Check if the S3 configuration is valid
 */
export const isS3Configured = (): boolean => {
  return !!(
    process.env.REACT_APP_AWS_ACCESS_KEY_ID &&
    process.env.REACT_APP_AWS_SECRET_ACCESS_KEY &&
    process.env.REACT_APP_S3_BUCKET
  );
};

